import { createTheme, Theme } from '@mui/material/styles';
import { PaletteMode } from '@mui/material';

// Clean Minimal Light Theme Palette (Stripe / MSCI / BlackRock Style)
export const lightFinanceColors = {
  bg: '#ffffff',           // Pure crisp white canvas
  bgSection: '#f8fafc',    // Soft slate light bg
  bgPaper: '#ffffff',      // Card white
  bgElevated: '#f1f5f9',   // Light slate container
  border: '#e2e8f0',       // Crisp border
  borderMuted: '#cbd5e1',  // Divider border
  textPrimary: '#0f172a',  // Deep Oxford Navy heading
  textSecondary: '#475569',// Slate secondary text
  accentEmerald: '#047857',// Institutional Forest Emerald
  accentBlue: '#1e40af',   // Oxford Corporate Blue
  accentPurple: '#6d28d9', // Deep Violet
  accentRose: '#be123c',   // Deep Crimson Red
  accentAmber: '#b45309',  // Warm Amber
};

export const createOneDarkProTheme = (mode: PaletteMode = 'light'): Theme => {
  return createTheme({
    palette: {
      mode: 'light',
      primary: {
        main: lightFinanceColors.accentEmerald,
        light: '#10b981',
        dark: '#064e3b',
        contrastText: '#ffffff',
      },
      secondary: {
        main: lightFinanceColors.accentBlue,
        light: '#3b82f6',
        dark: '#1e3a8a',
        contrastText: '#ffffff',
      },
      error: {
        main: lightFinanceColors.accentRose,
        light: '#f43f5e',
        dark: '#881337',
        contrastText: '#ffffff',
      },
      warning: {
        main: lightFinanceColors.accentAmber,
        light: '#f59e0b',
        dark: '#78350f',
        contrastText: '#ffffff',
      },
      info: {
        main: lightFinanceColors.accentBlue,
        light: '#60a5fa',
        dark: '#1d4ed8',
        contrastText: '#ffffff',
      },
      success: {
        main: lightFinanceColors.accentEmerald,
        light: '#34d399',
        dark: '#064e3b',
        contrastText: '#ffffff',
      },
      background: {
        default: lightFinanceColors.bg,
        paper: lightFinanceColors.bgPaper,
      },
      text: {
        primary: lightFinanceColors.textPrimary,
        secondary: lightFinanceColors.textSecondary,
        disabled: '#94a3b8',
      },
      divider: lightFinanceColors.border,
    },
    typography: {
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      h1: {
        fontFamily: '"Space Grotesk", "Inter", sans-serif',
        fontSize: '3.25rem',
        fontWeight: 800,
        letterSpacing: '-0.03em',
        lineHeight: 1.1,
        color: lightFinanceColors.textPrimary,
      },
      h2: {
        fontFamily: '"Space Grotesk", "Inter", sans-serif',
        fontSize: '2.5rem',
        fontWeight: 700,
        letterSpacing: '-0.02em',
        lineHeight: 1.2,
        color: lightFinanceColors.textPrimary,
      },
      h3: {
        fontFamily: '"Space Grotesk", "Inter", sans-serif',
        fontSize: '2rem',
        fontWeight: 700,
        letterSpacing: '-0.01em',
        color: lightFinanceColors.textPrimary,
      },
      h4: {
        fontFamily: '"Space Grotesk", "Inter", sans-serif',
        fontSize: '1.5rem',
        fontWeight: 600,
        color: lightFinanceColors.textPrimary,
      },
      h5: {
        fontSize: '1.25rem',
        fontWeight: 600,
        color: lightFinanceColors.textPrimary,
      },
      h6: {
        fontSize: '1rem',
        fontWeight: 600,
        color: lightFinanceColors.textPrimary,
      },
      body1: {
        fontSize: '1rem',
        lineHeight: 1.6,
        color: lightFinanceColors.textSecondary,
      },
      body2: {
        fontSize: '0.875rem',
        lineHeight: 1.5,
        color: lightFinanceColors.textSecondary,
      },
      button: {
        textTransform: 'none',
        fontWeight: 600,
        letterSpacing: '0.01em',
      },
    },
    shape: {
      borderRadius: 12,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: lightFinanceColors.bg,
            color: lightFinanceColors.textPrimary,
            margin: 0,
            overflowX: 'hidden',
            fontFamily: '"Inter", sans-serif',
            '& ::-webkit-scrollbar': {
              width: '8px',
              height: '8px',
            },
            '& ::-webkit-scrollbar-track': {
              background: '#f8fafc',
            },
            '& ::-webkit-scrollbar-thumb': {
              background: '#cbd5e1',
              borderRadius: '4px',
            },
            '& ::-webkit-scrollbar-thumb:hover': {
              background: '#94a3b8',
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundColor: lightFinanceColors.bgPaper,
            backgroundImage: 'none',
            border: `1px solid ${lightFinanceColors.border}`,
            borderRadius: 16,
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.05), 0 1px 2px 0 rgba(0, 0, 0, 0.03)',
            transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              borderColor: '#cbd5e1',
              boxShadow: '0 10px 25px -5px rgba(15, 23, 42, 0.08), 0 8px 10px -6px rgba(15, 23, 42, 0.04)',
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            textTransform: 'none',
            fontWeight: 600,
            padding: '10px 22px',
            transition: 'all 0.2s ease-in-out',
            boxShadow: 'none',
            '&.MuiButton-containedPrimary': {
              backgroundColor: lightFinanceColors.accentEmerald,
              color: '#ffffff',
              fontWeight: 700,
              boxShadow: '0 2px 8px rgba(4, 120, 87, 0.2)',
              '&:hover': {
                backgroundColor: '#065f46',
                boxShadow: '0 4px 14px rgba(4, 120, 87, 0.3)',
                transform: 'translateY(-1px)',
              },
            },
            '&.MuiButton-outlinedPrimary': {
              borderColor: lightFinanceColors.accentEmerald,
              color: lightFinanceColors.accentEmerald,
              '&:hover': {
                borderColor: '#065f46',
                backgroundColor: 'rgba(4, 120, 87, 0.05)',
              },
            },
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#ffffff',
              borderRadius: 8,
              '& fieldset': {
                borderColor: '#cbd5e1',
              },
              '&:hover fieldset': {
                borderColor: lightFinanceColors.accentEmerald,
              },
              '&.Mui-focused fieldset': {
                borderColor: lightFinanceColors.accentEmerald,
                borderWidth: '2px',
              },
            },
            '& .MuiInputBase-input': {
              color: lightFinanceColors.textPrimary,
            },
            '& .MuiInputLabel-root': {
              color: lightFinanceColors.textSecondary,
              '&.Mui-focused': {
                color: lightFinanceColors.accentEmerald,
              },
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            fontWeight: 600,
            fontFamily: '"JetBrains Mono", "Fira Code", monospace',
          },
        },
      },
    },
  });
};